import { create } from "zustand";
import { devtools } from "zustand/middleware";

import type { DemoId, IDemoSession } from "app/pages/sales-intelligence/types/demo-mode";

interface IDemoModeState {
    readonly activeDemo: IDemoSession | undefined;
    /** Chosen but not yet in effect: the banner is arriving and nothing else has changed. */
    readonly enteringDemo: IDemoSession | undefined;
    /** Begin entering a demo. Opens the banner; the demo takes effect on `commitEnteringDemo`. */
    readonly enterDemo: (session: IDemoSession) => void;
    /** Put the entering demo into effect: runs its `onEnter`, then activates it. No-op when none is entering. */
    readonly commitEnteringDemo: () => void;
    /** Exit the active demo: runs its `onExit`, then clears. Also abandons an entry still in flight. */
    readonly exitDemo: () => void;
}

/**
 * Generic demo-mode session store. Holds at most one demo and is the single funnel for enter/exit, so
 * lifecycle side effects (`onEnter`/`onExit`) run synchronously in the same tick as the state flip — every
 * exit trigger (banner close, in-feature return action, navigation away) routes through `exitDemo`.
 *
 * Entering is deliberately two-phase. Putting a demo into effect rebuilds the filters panel and refetches the
 * search — a sustained burst of main-thread work that leaves the banner's entrance animation no frames to
 * paint in. So `enterDemo` only opens the banner, and the banner promotes the session once it has finished
 * arriving. Consumers read `activeDemo` and therefore still see a single flip, never the intermediate phase.
 *
 * Non-persisted by design (a page reload clears it). A stale value surviving a single-spa unmount/remount is
 * cleared by calling `exitDemo` on re-entry into the Sales Intelligence shell.
 */
export const useDemoModeStore = create<IDemoModeState>()(
    devtools(
        (set, get) => ({
            activeDemo: undefined,
            enteringDemo: undefined,
            enterDemo: (session) => set({ enteringDemo: session }, false, "enterDemo"),
            commitEnteringDemo: () => {
                const { enteringDemo } = get();
                if (!enteringDemo) {
                    return;
                }
                enteringDemo.onEnter?.();
                set({ activeDemo: enteringDemo, enteringDemo: undefined }, false, "commitEnteringDemo");
            },
            exitDemo: () => {
                const { activeDemo, enteringDemo } = get();
                if (!activeDemo && !enteringDemo) {
                    return;
                }
                // Only a demo that took effect has anything to undo; an abandoned entry never ran `onEnter`.
                activeDemo?.onExit?.();
                set({ activeDemo: undefined, enteringDemo: undefined }, false, "exitDemo");
            },
        }),
        { name: "DemoModeStore" },
    ),
);

/* Pure store selectors — thin derivations of the store shape, collocated with it. Hooks that pull in external
 * dependencies (translation, routing) live under `hooks/demo-mode/` instead. */

/** True when any demo is in effect. Does not re-render on route changes. */
export const useIsDemoMode = (): boolean => useDemoModeStore((state) => state.activeDemo !== undefined);

/** The demo in effect, or undefined. */
export const useActiveDemo = (): IDemoSession | undefined => useDemoModeStore((state) => state.activeDemo);

/** The id of the demo in effect, or undefined. Lets a consumer react only to *its* demo, not any demo. */
export const useActiveDemoId = (): DemoId | undefined => useDemoModeStore((state) => state.activeDemo?.id);

/** The session awaiting activation, or undefined. Only the banner, which drives the promotion, needs this. */
export const useEnteringDemo = (): IDemoSession | undefined => useDemoModeStore((state) => state.enteringDemo);

/**
 * The session the banner shows: the one arriving, else the one in effect — the banner leads activation, so
 * this is deliberately not `useActiveDemo`.
 */
export const useVisibleDemo = (): IDemoSession | undefined =>
    useDemoModeStore((state) => state.enteringDemo ?? state.activeDemo);